(function (root, factory) {
  const bridge = factory(typeof module === 'object' && module.exports
    ? require('./black-friday-gift-rules.js') : root.LRBlackFridayGiftRules);
  if (typeof module === 'object' && module.exports) module.exports = bridge;
  else bridge.mount(root);
})(typeof globalThis !== 'undefined' ? globalThis : this, function (rules) {
  'use strict';
  const format = (template, values = {}) => String(template || '').replace(/\{(\w+)\}/g,
    (match, key) => values[key] === undefined ? match : String(values[key]));
  const decodeText = (value) => String(value ?? '').replace(/&(#x[\da-f]+|#\d+|amp|lt|gt|quot|apos|nbsp);/gi, (entity, key) => {
    if (key[0] !== '#') return ({ amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: '\u00a0' })[key.toLowerCase()];
    const code = key[1].toLowerCase() === 'x' ? parseInt(key.slice(2), 16) : Number(key.slice(1));
    return code > 0 && code <= 0x10ffff && !(code >= 0xd800 && code <= 0xdfff) ? String.fromCodePoint(code) : entity;
  });
  const decodeStrings = (data) => ({ ...data, strings: Object.fromEntries(
    Object.entries(data.strings || {}).map(([key, value]) => [key, decodeText(value)])) });
  const fingerprint = (cart) => JSON.stringify((cart.items || []).map((item) =>
    [String(item.key), Number(item.quantity), Number(item.original_line_price)]).sort());
  const displayFingerprint = (cart) => JSON.stringify([fingerprint(cart), cart.item_count, cart.items_subtotal_price,
    (cart.items || []).map((item) => [String(item.key), item.final_line_price]).sort(), cart.discount_codes]);
  const codes = (value) => [...new Set(String(value || '').split(/[,\n]+/).map((code) => code.trim()).filter(Boolean))];
  function active(data, now = Date.now()) {
    if (!data.enabled || data.currency !== 'USD') return false;
    const zonedTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/;
    if (data.startsAt && !zonedTime.test(data.startsAt) || data.endsAt && !zonedTime.test(data.endsAt)) return false;
    const starts = data.startsAt ? Date.parse(data.startsAt) : null;
    const ends = data.endsAt ? Date.parse(data.endsAt) : null;
    if (data.startsAt && !Number.isFinite(starts) || data.endsAt && !Number.isFinite(ends)) return false;
    if (data.backendMode === 'test_codes' && (starts === null || ends === null || codes(data.testCodes).length !== 5)) return false;
    if (!['test_codes', 'automatic'].includes(data.backendMode)) return false;
    if (starts !== null && ends !== null && starts >= ends) return false;
    return (starts === null || now >= starts) && (ends === null || now < ends);
  }
  function mergedCodes(cart, data, extra = []) {
    if (!Array.isArray(cart.discount_codes)) throw new Error('Discount state unavailable / 优惠码状态不可完整读取');
    const owned = new Set(codes(data.testCodes).map((code) => code.toUpperCase()));
    const existing = cart.discount_codes.map((entry) => entry.code).filter(Boolean)
      .filter((code) => !owned.has(String(code).toUpperCase()));
    const giftIds = new Set(data.dedicatedGiftVariantIds.map(String));
    const selectedCount = (cart.items || []).filter((item) => giftIds.has(String(item.variant_id)))
      .reduce((sum, item) => sum + Number(item.quantity), 0);
    const selectedCode = active(data) && selectedCount > 0 && selectedCount <= rules.MAX_GIFTS
      ? codes(data.testCodes).slice(selectedCount - 1, selectedCount) : [];
    const entered = extra.filter((code) => !owned.has(String(code).toUpperCase()));
    return [...new Set([...existing, ...entered, ...selectedCode])].join(',');
  }
  function freeGiftsConfirmed(cart, identity) {
    const giftIds = new Set(identity.dedicatedGiftVariantIds.map(String));
    const gifts = cart.items.filter((item) => giftIds.has(String(item.variant_id)));
    return gifts.every((item) => Number.isSafeInteger(Number(item.final_line_price)) &&
      Number(item.final_line_price) === 0);
  }
  function resultFor(cart, data) {
    let enabled = active(data);
    let tiers = null;
    let uiEnabled = false;
    const qualificationScope = data.qualificationScope || 'collection_original';
    const wholeCart = data.unified && qualificationScope === 'cart_items_subtotal';
    const validWholeCart = wholeCart && Number.isSafeInteger(cart.items_subtotal_price) && cart.items_subtotal_price >= 0 &&
      Array.isArray(data.rewardTiers) && data.rewardTiers.filter(tier => tier.enabled !== false).every(tier => tier.rewardType === 'discount');
    if (wholeCart) enabled = false;
    if (data.unified) {
      try {
        tiers = rules.effectiveTiers(data.rewardTiers);
        uiEnabled = Boolean(data.rewardsEnabled && data.currency === 'USD' && tiers.length &&
          (qualificationScope === 'collection_original' || validWholeCart));
      } catch { enabled = false; }
    }
    if (enabled) {
      try { rules.rewardState(0, data.rewardTiers); } catch { enabled = false; }
    }
    const result = rules.reconcile(cart, data.participantProductIds,
      data.catalog, [], enabled, { ...data, strictDedicatedIdentity: true });
    if (uiEnabled) {
      const displayTiers = enabled ? tiers : tiers.filter((tier, index) => tier.discountValue > 0 &&
        (!index || tier.discountValue !== tiers[index - 1].discountValue || tier.discountType !== tiers[index - 1].discountType))
        .map(tier => ({ ...tier, giftCount: 0 }));
      if (validWholeCart) result.state = rules.rewardState(cart.items_subtotal_price, displayTiers);
      const current = displayTiers.filter(tier => result.state.subtotalCents >= tier.thresholdCents).at(-1);
      result.state = { ...result.state, giftCount: enabled ? result.state.giftCount : 0,
        discountType: current?.discountType || 'percentage', discountValue: current?.discountValue || 0,
        discountPercent: current?.discountPercent || 0 };
      tiers = displayTiers;
    }
    return { data, enabled, uiEnabled, tiers, discountConfirmed: freeGiftsConfirmed(cart, data), ...result };
  }
  function sectionTargets(entries) {
    return entries.filter((entry) => typeof entry.section === 'string' && entry.section.length > 0);
  }
  function replaceCartSection(target, source) {
    const previous = target.querySelectorAll('cart-recommendations');
    const incoming = source.querySelectorAll('cart-recommendations');
    const pathTo = (parent, node) => {
      const path = [];
      while (node && node !== parent) { path.unshift(node); node = node.parentElement; }
      return node === parent ? path : [];
    };
    const oldPath = previous.length === 1 ? pathTo(target, previous[0]) : [];
    const newPath = incoming.length === 1 ? pathTo(source, incoming[0]) : [];
    if (!oldPath.length || oldPath.length !== newPath.length || oldPath.some((node, index) =>
      node.nodeName !== newPath[index].nodeName || node.id !== newPath[index].id)) {
      target.innerHTML = source.innerHTML;
      return false;
    }
    const scroll = [target, ...oldPath.slice(0, -1)].map((node) => ({ node, top: node.scrollTop, left: node.scrollLeft }));
    oldPath.forEach((branch, index) => {
      const parent = index ? oldPath[index - 1] : target;
      const nextParent = index ? newPath[index - 1] : source;
      const nextBranch = newPath[index];
      const children = Array.from(nextParent.childNodes);
      const branchIndex = children.indexOf(nextBranch);
      Array.from(parent.childNodes).forEach((node) => { if (node !== branch) node.remove(); });
      children.slice(0, branchIndex).forEach((node) => parent.insertBefore(node.cloneNode(true), branch));
      children.slice(branchIndex + 1).forEach((node) => parent.append(node.cloneNode(true)));
      if (branch === previous[0]) return;
      Array.from(branch.attributes).forEach((attribute) => {
        if (!nextBranch.hasAttribute(attribute.name)) branch.removeAttribute(attribute.name);
      });
      Array.from(nextBranch.attributes).forEach((attribute) => {
        if (branch.getAttribute(attribute.name) !== attribute.value) branch.setAttribute(attribute.name, attribute.value);
      });
    });
    scroll.forEach(({ node, top, left }) => { node.scrollTop = top; node.scrollLeft = left; });
    return true;
  }
  function createClient({ fetch, loadData, root = '/', onBusy = () => {}, onChange = () => {},
    getMutationSections = () => [], readResponseData = () => null, readResponseCart = () => null,
    sectionsUrl = root, onTiming = () => {}, onInvalidate = () => {} }) {
    let tail = Promise.resolve();
    let pending = 0;
    let notBefore = 0;
    let revision = 0;
    let confirmed = null;
    let syncPending = null;
    const publish = (current) => {
      confirmed = { ...current, revision };
      onChange(confirmed);
      return confirmed;
    };
    const runCartMutation = (operation, label = 'cart') => {
      const queuedAt = Date.now();
      pending += 1;
      revision += 1;
      onInvalidate(revision);
      onBusy(true);
      const task = tail.then(async () => {
        const startedAt = Date.now();
        try { return await operation(); }
        finally { onTiming({ step: label, queuedMs: startedAt - queuedAt, elapsedMs: Date.now() - startedAt }); }
      });
      tail = task.catch(() => {});
      return task.finally(() => { pending -= 1; onBusy(pending > 0); });
    };
    async function request(path, body) {
      if (Date.now() < notBefore) throw new Error('Requests paused / 请求暂时暂停');
      const sections = body ? [...new Set(getMutationSections(path, body))].filter(Boolean).slice(0, 5) : [];
      if (sections.length) body = { ...body, sections, sections_url: sectionsUrl };
      const startedAt = Date.now();
      const response = await fetch(`${root}${path}`, { credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}) });
      if (response.status === 429) {
        const retry = response.headers?.get?.('Retry-After');
        const delay = Number.isFinite(Number(retry)) ? Number(retry) * 1000 : Date.parse(retry) - Date.now();
        notBefore = Date.now() + Math.max(30000, delay || 0);
      }
      if (!response.ok) throw new Error(`Cart request failed / 购物车请求失败 (${response.status})`);
      const value = await response.json();
      onTiming({ step: path, elapsedMs: Date.now() - startedAt, sections: sections.length, status: response.status });
      if (value.errors || value.status) throw new Error('Cart update rejected / 购物车更新被拒绝');
      return value;
    }
    async function read(seedCart, seedData) {
      const [cart, data] = await Promise.all([seedCart || request('cart.js'), seedData || loadData()]);
      if (cart.currency !== 'USD' || fingerprint(cart) !== fingerprint(data)) {
        throw new Error('Cart state changed / 购物车状态已变化');
      }
      return { cart, data, result: resultFor(cart, data) };
    }
    async function afterWrite(response, previous) {
      let data = null;
      try { data = readResponseData(response); } catch { /* Re-read if a section failed to render. */ }
      const fullCart = response.currency && Array.isArray(response.items) && Array.isArray(response.discount_codes);
      let provisional = null;
      if (!fullCart && data?.backendMode === 'test_codes' && Array.isArray(previous?.cart.discount_codes)) {
        try { provisional = readResponseCart(data); } catch { /* Use a full fresh lookup if the snapshot is incomplete. */ }
        if (provisional) provisional = { ...provisional, discount_codes: previous.cart.discount_codes };
      }
      const current = await read(fullCart ? response : provisional, data);
      if (provisional) current.discountCodesCarried = true;
      if (response.sections) current.cart.sections = response.sections;
      return current;
    }
    async function clean(seed) {
      let current = seed || await read();
      for (let attempt = 0; attempt < 3; attempt += 1) {
        if (!Object.keys(current.result.updates).length) return current;
        current = await afterWrite(await request('cart/update.js', { updates: current.result.updates }));
      }
      if (Object.keys(current.result.updates).length) throw new Error('Gift reconciliation incomplete / 赠品校验未完成');
      return current;
    }
    async function applyCodes(current, extra) {
      if (current.data.backendMode !== 'test_codes') return current;
      const discount = mergedCodes(current.cart, current.data, extra);
      const before = current.cart.discount_codes.map((entry) => entry.code).filter(Boolean).join(',');
      if (discount !== before || current.discountCodesCarried) {
        current = await clean(await afterWrite(await request('cart/update.js', { discount })));
      }
      return current;
    }
    const sync = () => {
      if (syncPending?.revision === revision) return syncPending.task;
      const task = runCartMutation(async () => {
      let current = await applyCodes(await clean());
      if (current.result.selectedCount) {
        if (!freeGiftsConfirmed(current.cart, current.data)) {
          throw new Error('Gift discount not confirmed / 赠品减免尚未确认');
        }
      }
        return publish(current);
      }, 'sync');
      const entry = { revision, task };
      syncPending = entry;
      void task.finally(() => { if (syncPending === entry) syncPending = null; }).catch(() => {});
      return task;
    };
    const selectMany = (changes) => runCartMutation(async () => {
      if (!Array.isArray(changes) || !changes.length || changes.length > rules.MAX_GIFTS * 2 ||
        changes.some((entry) => !Number.isSafeInteger(entry.delta) || !entry.delta || Math.abs(entry.delta) > rules.MAX_GIFTS)) {
        throw new Error('Invalid gift action / 无效赠品操作');
      }
      let current = await clean();
      const lines = current.result.kept.map((item) => ({ ...item }));
      for (const entry of changes) {
        const id = String(entry.variantId);
        if (!current.data.dedicatedGiftVariantIds.some((value) => String(value) === id)) {
          throw new Error('Gift unavailable / 赠品不可选');
        }
        if (entry.delta > 0) lines.push({ variantId: id, quantity: entry.delta });
        else {
          let remaining = -entry.delta;
          for (let index = lines.length - 1; index >= 0 && remaining; index -= 1) {
            if (lines[index].variantId !== id) continue;
            const removed = Math.min(remaining, lines[index].quantity);
            lines[index].quantity -= removed;
            remaining -= removed;
          }
        }
      }
      const kept = lines.filter((item) => item.quantity > 0);
      const additions = kept.filter((item) => !item.key);
      const counts = new Map();
      kept.forEach((item) => counts.set(item.variantId, (counts.get(item.variantId) || 0) + item.quantity));
      if (additions.length && (!current.result.enabled ||
        kept.reduce((sum, item) => sum + item.quantity, 0) > current.result.state.giftCount ||
        [...counts].some(([id, quantity]) => quantity > (current.data.catalog.find((gift) => String(gift.variantId) === id)?.availableQuantity || 0)))) {
        throw new Error('Gift unavailable / 赠品不可选');
      }
      if (current.data.backendMode === 'test_codes') mergedCodes(current.cart, current.data);
      const updates = {};
      current.result.kept.forEach((item) => {
        const quantity = kept.find((line) => line.key === item.key)?.quantity || 0;
        if (quantity !== item.quantity) updates[item.key] = quantity;
      });
      if (Object.keys(updates).length) {
        const nextCart = { ...current.cart, items: current.cart.items.map((item) =>
          updates[item.key] === undefined ? item : { ...item, quantity: updates[item.key] }) };
        current = await clean(await afterWrite(await request('cart/update.js', { updates,
          ...(current.data.backendMode === 'test_codes' ? { discount: mergedCodes(nextCart, current.data) } : {}) }), current));
      }
      if (additions.length) {
        const needed = new Map();
        additions.forEach((item) => needed.set(item.variantId, (needed.get(item.variantId) || 0) + item.quantity));
        if (!current.result.enabled || additions.reduce((sum, item) => sum + item.quantity, 0) > current.result.remainingSlots ||
          [...needed].some(([id, quantity]) => quantity > (current.result.availableGifts.find((gift) => String(gift.variantId) === id)?.remainingStock || 0))) {
          throw new Error('Gift unavailable / 赠品不可选');
        }
        const lastOrder = Math.max(0, ...current.cart.items.map((item) => Number(item.properties?._lr_bf_order) || 0));
        const order = Math.max(Date.now(), lastOrder + 1);
        const response = await request('cart/add.js', { items: additions.map((item, index) => ({
          id: Number(item.variantId), quantity: item.quantity, properties: {
            [rules.GIFT_PROPERTY]: rules.CAMPAIGN_ID, _lr_bf_order: String(order + index),
          }
        })) });
        current = await clean(await afterWrite(response, current));
      }
      current = await applyCodes(current);
      return publish(current);
    }, 'select-batch');
    const select = (variantId, delta) => Math.abs(delta) === 1
      ? selectMany([{ variantId, delta }]) : Promise.reject(new Error('Invalid gift action / 无效赠品操作'));
    const checkout = (extra = []) => runCartMutation(async () => {
      const current = await applyCodes(await clean(), extra);
      if (!freeGiftsConfirmed(current.cart, current.data)) {
        throw new Error('Gift discount not confirmed / 赠品减免尚未确认');
      }
      return publish(current);
    }, 'checkout');
    return { runCartMutation, read, sync, select, selectMany, checkout,
      revision: () => revision,
      displayFor: (cart) => confirmed && displayFingerprint(cart) === displayFingerprint(confirmed.cart) ? confirmed.result : null,
      observeResponse: (response) => {
        let data;
        try { data = readResponseData(response); } catch { return null; }
        if (!data) return null;
        const cart = response.currency && Array.isArray(response.items) ? response : readResponseCart(data);
        if (!cart || fingerprint(cart) !== fingerprint(data)) return null;
        confirmed = { cart, data, result: resultFor(cart, data), revision };
        return confirmed;
      },
      prepare: async (cart) => {
        const data = await loadData();
        if (cart.currency !== 'USD' || fingerprint(cart) !== fingerprint(data)) {
          throw new Error('Cart state changed / 购物车状态已变化');
        }
        return resultFor(cart, data);
      }, busy: () => pending > 0 };
  }
  function createSelectionQueue({ client, onUpdate = () => {}, onResult = () => {}, onError = () => {},
    delay = 180, schedule = (callback, ms) => setTimeout(callback, ms), cancel = (id) => clearTimeout(id) }) {
    let confirmed = null;
    let queued = [];
    let sending = [];
    let timer = null;
    let running = null;
    let blocked = false;
    let paused = false;
    const snapshot = () => {
      const counts = new Map();
      confirmed?.kept.forEach((item) => counts.set(item.variantId, (counts.get(item.variantId) || 0) + item.quantity));
      [...sending, ...queued].forEach(({ variantId, delta }) => counts.set(variantId, Math.max(0, (counts.get(variantId) || 0) + delta)));
      return { counts, pending: !!(queued.length || sending.length), blocked,
        selectedCount: [...counts.values()].reduce((sum, quantity) => sum + quantity, 0) };
    };
    const notify = () => onUpdate(snapshot());
    async function drain() {
      if (timer !== null) { cancel(timer); timer = null; }
      if (running) { await running; return drain(); }
      if (blocked) throw new Error('Gift check required / 赠品需要重新校验');
      if (!queued.length) return;
      sending = queued;
      queued = [];
      running = (async () => {
        try {
          const current = await client.selectMany(sending);
          if (!current.result.discountConfirmed) throw new Error('Gift discount not confirmed / 赠品减免尚未确认');
          confirmed = current.result;
          sending = [];
          notify();
          await onResult(current);
        } catch (failure) {
          queued = [];
          sending = [];
          blocked = true;
          await onError(failure);
          throw failure;
        } finally { running = null; notify(); }
      })();
      await running;
      if (queued.length) return drain();
    }
    function enqueue(variantId, delta) {
      const id = String(variantId);
      if (!confirmed || blocked || paused || !Number.isSafeInteger(delta) || !delta || Math.abs(delta) > rules.MAX_GIFTS) return false;
      const state = snapshot();
      const count = state.counts.get(id) || 0;
      const stock = confirmed.data.catalog.find((gift) => String(gift.variantId) === id)?.availableQuantity || 0;
      if (delta > 0 && (!confirmed.enabled || state.selectedCount + delta > confirmed.state.giftCount || count + delta > stock) || count + delta < 0) return false;
      for (let unit = 0; unit < Math.abs(delta); unit += 1) {
        const lastAdd = delta < 0 ? queued.findLastIndex((item) => item.variantId === id && item.delta > 0) : -1;
        if (lastAdd >= 0) queued.splice(lastAdd, 1);
        else queued.push({ variantId: id, delta: Math.sign(delta) });
      }
      if (timer !== null) cancel(timer);
      timer = queued.length && !running ? schedule(() => { timer = null; void drain().catch(() => {}); }, delay) : null;
      notify();
      return true;
    }
    return { enqueue, snapshot, flush: drain,
      observe(result) { if (!sending.length) confirmed = result; },
      pause(value) { paused = value; },
      recover(result) { confirmed = result; blocked = false; notify(); } };
  }
  function mount(window) {
    const document = window.document;
    const initialNode = document.querySelector('[data-bf-gifts-data]');
    if (!initialNode || !rules) return;
    let initial;
    try { initial = decodeStrings(JSON.parse(initialNode.textContent)); } catch { return; }
    const armed = initial.unified && initial.rewardsEnabled || initial.requested || initial.dedicatedGiftVariantIds.some((id) =>
      initial.items.some((item) => String(item.variant_id) === String(id)));
    if (!armed) return;
    const nativeFetch = window.fetch.bind(window);
    const root = window.Shopify?.routes?.root || '/';
    const strings = initial.strings;
    const timingEnabled = new URLSearchParams(window.location.search).get('lr_bf_timing') === '1';
    const reportTiming = (value) => {
      if (timingEnabled) window.console.info('[LR gifts timing / 赠品耗时]', JSON.stringify(value));
    };
    let busy = false;
    let notice = '';
    let error = false;
    let lastResult = null;
    let syncing = false;
    let sectionRead = null;
    let latestData = initial;
    let latestDataAt = Date.now();
    let mutationVersion = 0;
    let resumeCheckout = false;
    let checkoutRunning = false;
    let editingGifts = false;
    let giftFocus = null;
    document.addEventListener('focusin', (event) => {
      const button = event.target.closest('[data-bf-variant]');
      giftFocus = button ? { id: button.dataset.bfVariant, delta: button.dataset.bfDelta,
        scope: button.closest('[data-bf-inline-gifts]') ? 'drawer'
          : button.closest('[data-bf-gift-surface]') ? 'picker'
          : button.closest('[data-cart-rewards]')?.dataset.cartRewards } : null;
    });
    document.addEventListener('pointerdown', (event) => {
      if (!event.target.closest('[data-bf-variant]')) giftFocus = null;
    });
    let picker = null;
    let selectionStartedAt = 0;
    const escape = (value) => String(value ?? '').replace(/[&<>"']/g, (char) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
    const money = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
    function detailsUrl(gift) {
      try {
        const url = new URL(gift.details?.url, window.location.origin);
        return url.origin === window.location.origin && /\/products\/[^/]+$/.test(url.pathname)
          ? url.pathname + url.search : null;
      } catch { return null; }
    }
    function readResponseData(response) {
      const text = response.sections?.['black-friday-gifts-state'];
      if (!text) return null;
      const node = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-bf-gifts-data]');
      if (!node) return null;
      latestData = decodeStrings(JSON.parse(node.textContent));
      latestDataAt = Date.now();
      return latestData;
    }
    function cartFromData(data) {
      if (!Number.isSafeInteger(data?.itemCount) || !Number.isSafeInteger(data?.itemsSubtotalPrice) ||
        !data.items.every((item) => Number.isSafeInteger(item.final_line_price))) return null;
      return { currency: data.currency, item_count: data.itemCount,
        items_subtotal_price: data.itemsSubtotalPrice, items: data.items };
    }
    function renderTargets() {
      const cartItems = document.querySelector('cart-items');
      return sectionTargets(cartItems?.getSectionsToRender?.() || document.querySelector('mini-cart')?.getSectionsToRender?.() || [])
        .filter((entry) => entry.id !== 'cart-live-region-text');
    }
    async function loadData() {
      if (sectionRead) return sectionRead;
      sectionRead = (async () => {
        const startedAt = Date.now();
        const url = new URL(root, window.location.origin);
        url.searchParams.set('section_id', 'black-friday-gifts-state');
        const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await nativeFetch(url, { cache: 'no-store', credentials: 'same-origin' });
        if (!response.ok) throw new Error('Gift state unavailable / 赠品状态不可用');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = html.querySelector('[data-bf-gifts-data]');
        if (!node) throw new Error('Gift state missing / 缺少赠品状态');
        latestData = decodeStrings(JSON.parse(node.textContent));
        latestDataAt = Date.now();
        reportTiming({ step: 'gift-state', elapsedMs: Date.now() - startedAt });
        return latestData;
      })();
      try { return await sectionRead; } finally { sectionRead = null; }
    }
    const client = createClient({ fetch: nativeFetch, loadData, root,
      onTiming: reportTiming,
      onInvalidate() { mutationVersion += 1; },
      sectionsUrl: window.location.pathname + window.location.search,
      getMutationSections: (path, body) => ['black-friday-gifts-state',
        ...(latestData.backendMode !== 'test_codes' || body.discount !== undefined
          ? renderTargets().map((entry) => entry.section) : [])],
      readResponseData,
      readResponseCart: cartFromData,
      onBusy(value) {
        busy = value;
        document.documentElement.classList.toggle('bf-gifts-busy', value);
        if (lastResult) render(lastResult, { reconcile: false });
      },
      onChange(current) { document.dispatchEvent(new CustomEvent('bf-gifts:changed', { detail: current })); }
    });
    const selections = createSelectionQueue({ client,
      onUpdate(state) {
        if (state.pending && !selectionStartedAt) selectionStartedAt = Date.now();
        if (lastResult) render(lastResult, { reconcile: false });
        if (!state.pending && selectionStartedAt) {
          reportTiming({ step: 'selection-confirmed', elapsedMs: Date.now() - selectionStartedAt,
            status: state.blocked ? 'error' : 'ok' });
          selectionStartedAt = 0;
        }
      },
      async onResult(current) {
        clearErrors();
        render(current.result);
        if (!selections.snapshot().pending) await refreshSections(current);
      },
      async onError() {
        showError();
        try {
          const current = await client.runCartMutation(() => client.read(), 'reconcile-failure');
          render(current.result, { reconcile: false });
          await refreshSections(current);
        } catch {}
      }
    });
    function patchSurface(target, source) {
      const children = Array.from(source.childNodes);
      children.forEach((next, index) => {
        let current = target.childNodes[index];
        const key = next.nodeType === 1 ? next.getAttribute('data-bf-card') : null;
        if (key && current?.getAttribute?.('data-bf-card') !== key) {
          const keyed = Array.from(target.children).find((node) => node.getAttribute('data-bf-card') === key);
          if (keyed) { target.insertBefore(keyed, current || null); current = keyed; }
        }
        if (!current) { target.append(next.cloneNode(true)); return; }
        if (current.nodeType !== next.nodeType || current.nodeName !== next.nodeName) {
          current.replaceWith(next.cloneNode(true));
          return;
        }
        if (next.nodeType !== 1) {
          if (current.nodeValue !== next.nodeValue) current.nodeValue = next.nodeValue;
          return;
        }
        Array.from(current.attributes).forEach((attribute) => {
          if (!next.hasAttribute(attribute.name)) current.removeAttribute(attribute.name);
        });
        Array.from(next.attributes).forEach((attribute) => {
          if (current.getAttribute(attribute.name) !== attribute.value) current.setAttribute(attribute.name, attribute.value);
        });
        patchSurface(current, next);
      });
      while (target.childNodes.length > children.length) target.lastChild.remove();
    }
    function render(result, { reconcile = true } = {}) {
      selections.observe(result);
      const selection = selections.snapshot();
      if (lastResult && (result.selectedCount !== lastResult.selectedCount ||
        result.state.giftCount !== lastResult.state.giftCount)) editingGifts = false;
      lastResult = result;
      const collapseReady = !selection.pending && !editingGifts && !error && result.discountConfirmed &&
        result.state.giftCount > 0 && result.selectedCount === result.state.giftCount &&
        !Object.keys(result.updates).length;
      document.documentElement.classList.toggle('bf-gifts-active', result.enabled || result.uiEnabled);
      document.querySelectorAll('[data-cart-rewards], [data-bf-gift-surface]').forEach((progressHost) => {
        let rewardHost = progressHost;
        if (progressHost.dataset.cartRewards === 'drawer') {
          const items = progressHost.nextElementSibling?.querySelector(':scope > cart-items');
          if (items) {
            let surface = items.querySelector(':scope > [data-bf-inline-gifts]');
            if (!surface) {
              surface = document.createElement('div');
              surface.className = 'bf-gifts-inline';
              surface.dataset.bfInlineGifts = '';
              items.prepend(surface);
            }
            const existingGifts = progressHost.querySelector('[data-bf-gifts]');
            if (existingGifts) surface.append(existingGifts);
            rewardHost = surface;
          }
        }
        const inPicker = rewardHost.hasAttribute('data-bf-gift-surface');
        const inline = rewardHost.hasAttribute('data-bf-inline-gifts');
        const collapsed = !inPicker && !inline && collapseReady;
        let host = rewardHost.querySelector('[data-bf-gifts]');
        if (!host) {
          host = document.createElement('section');
          host.className = 'bf-gifts';
          host.dataset.bfGifts = '';
          rewardHost.append(host);
        }
        host.hidden = !result.enabled || result.state.giftCount < 1;
        host.setAttribute('aria-busy', String(busy && !selection.pending));
        const selected = selection.counts;
        const locked = selection.blocked || checkoutRunning;
        const cards = result.data.catalog.filter((gift) => gift.availableQuantity > 0).map((gift) => {
          const id = String(gift.variantId);
          const count = selected.get(id) || 0;
          const confirmedCount = result.kept.filter((item) => item.variantId === id).reduce((sum, item) => sum + item.quantity, 0);
          const unavailable = locked || !result.enabled || selection.selectedCount >= result.state.giftCount || count >= gift.availableQuantity;
          return `<li class="bf-gifts__card${count ? ' is-selected' : unavailable ? ' is-unavailable' : ''}" data-bf-card="${escape(id)}" aria-busy="${count !== confirmedCount}"${count !== confirmedCount ? ' data-bf-pending' : ''}><div class="bf-gifts__thumb">${gift.image ? `<img class="bf-gifts__image" src="${escape(gift.image)}" alt="" width="60" height="60" loading="${inPicker ? 'eager' : 'lazy'}" decoding="async">` : ''}<span class="bf-gifts__selected-mark" aria-hidden="true"${count ? '' : ' hidden'}><svg viewBox="0 0 12 12"><path d="m2 6 2.5 2.5L10 3" fill="none" stroke="currentColor" stroke-width="1.8"/></svg></span></div>
            <div class="bf-gifts__content"><p class="bf-gifts__name">${detailsUrl(gift) ? `<a href="${escape(detailsUrl(gift))}" data-bf-detail="${escape(id)}" aria-label="${escape(format(strings.details, { title: gift.title }))}">${escape(gift.title)}</a>` : escape(gift.title)}</p><p class="bf-gifts__price">${Number.isSafeInteger(gift.priceCents) && gift.priceCents > 0 ? `<s>${escape(money(gift.priceCents))}</s>` : ''}<b>${escape(strings.free_label || 'FREE')}</b></p><div class="bf-gifts__quantity${count ? '' : ' is-empty'}">
              <button type="button" data-bf-delta="-1" data-bf-variant="${escape(id)}" aria-label="${escape(format(strings.remove, { title: gift.title }))}"${!count ? ' hidden' : ''}${locked || !count ? ' disabled' : ''}>−</button>
              <output aria-label="${escape(gift.title)}"${!count ? ' hidden' : ''}>${count}</output>
              <button type="button" data-bf-delta="1" data-bf-variant="${escape(id)}" aria-label="${escape(format(strings.add, { title: gift.title }))}"${unavailable ? ' disabled' : ''}>${count ? '+' : escape(strings.add_label || 'Add')}</button>
            </div><span class="bf-gifts__row-status" role="status">${count !== confirmedCount ? escape(strings.updating) : ''}</span></div></li>`;
        }).join('');
        const status = format(strings.selected, { selected: result.selectedCount, allowed: result.state.giftCount });
        const allowed = result.state.giftCount;
        const remaining = Math.max(0, allowed - selection.selectedCount);
        const nextGift = (result.tiers || result.data.rewardTiers).find((tier) => tier.giftCount > allowed && tier.thresholdCents > result.state.subtotalCents);
        const firstGift = (result.tiers || result.data.rewardTiers).find((tier) => tier.giftCount > 0);
        const hint = !allowed && firstGift
          ? format(strings.unlock_first, {amount:money(firstGift.thresholdCents),remaining:money(Math.max(0,firstGift.thresholdCents-result.state.subtotalCents))})
          : remaining ? format(remaining === 1 ? strings.pick_one : strings.pick_other,{count:remaining})
          : selection.pending || !result.discountConfirmed
            ? host.querySelector('.bf-gifts__hint')?.textContent || status : strings.all_set;
        const dots = Array.from({length:Math.min(5,allowed)},(_,i)=>`<i${i < selection.selectedCount ? ' class="is-filled"' : ''}></i>`).join('');
        const nextMessage = nextGift ? format(nextGift.giftCount-allowed === 1 ? strings.unlock_more_one : strings.unlock_more_other,
          {amount:money(nextGift.thresholdCents-result.state.subtotalCents),count:nextGift.giftCount-allowed}) : '';
        const markup = `<div class="bf-gifts__heading"><h3 class="bf-gifts__title">${escape(collapsed ? strings.selected_title : strings.title)}</h3>
          <span class="bf-gifts__count"><span class="bf-gifts__dots" aria-hidden="true">${dots}</span>${escape(allowed ? format(strings.count_short,{selected:selection.selectedCount,allowed}) : strings.locked_label)}</span>
          <button type="button" class="bf-gifts__edit" data-bf-edit aria-expanded="${!collapsed}"${collapsed ? '' : ' hidden'}>${escape(strings.edit)}</button></div>
          <p class="bf-gifts__status" role="status">${escape(status)}</p><p class="bf-gifts__hint">${escape(hint)}</p>
          <p class="bf-gifts__pending" role="status"${busy && !selection.pending ? '' : ' hidden'}>${escape(strings.updating || 'Updating gifts…')}</p>
          <div data-bf-choices${collapsed ? ' hidden' : ''}>
          <ul class="bf-gifts__list">${cards}</ul>${!cards ? `<p>${escape(strings.unavailable)}</p>` : ''}
          </div>
          ${nextMessage && allowed ? `<p class="bf-gifts__next"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="3" y="7" width="10" height="7" rx="2"/><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2"/></svg><span>${escape(nextMessage).replace(/\$[\d,]+(?:\.\d{2})?/g, amount=>`<b>${amount}</b>`)}</span></p>` : ''}
          <p class="bf-gifts__notice${error ? ' bf-gifts__error' : ''}" role="status"${!notice ? ' hidden' : ''}>${escape(notice)}</p>
          ${error ? `<button type="button" class="button button--secondary" data-bf-retry>${escape(strings.retry)}</button>` : ''}`;
        if (host.dataset.bfMarkup !== markup) {
          const focused = host.contains(document.activeElement) ? document.activeElement : null;
          const rememberedFocus = document.activeElement === document.body && giftFocus?.scope === (progressHost.dataset.cartRewards || 'picker') ? giftFocus : null;
          const focusId = focused?.dataset.bfVariant || rememberedFocus?.id;
          const focusDelta = focused?.dataset.bfDelta || rememberedFocus?.delta;
          const template = document.createElement('template');
          template.innerHTML = markup;
          patchSurface(host, template.content);
          host.dataset.bfMarkup = markup;
          if (focusId) {
            const card = host.querySelector(`[data-bf-card="${CSS.escape(focusId)}"]`);
            const available = (node) => node && !node.disabled && !node.closest('[hidden]') && node.getClientRects().length > 0;
            const preferred = collapsed ? host.querySelector('[data-bf-edit]')
              : card?.querySelector(`[data-bf-delta="${focusDelta}"]`);
            const target = available(preferred) ? preferred
              : [...(card?.querySelectorAll('button') || []), ...(card?.querySelectorAll('[data-bf-detail]') || [])].find(available);
            target?.focus();
          }
          else if (focused?.hasAttribute('data-bf-edit')) host.querySelector('[data-bf-delta="-1"]:not([disabled])')?.focus();
        }
      });
      const needsGiftSync = Object.keys(result.updates).length ||
        result.enabled && result.selectedCount > 0 && !result.discountConfirmed && result.data.backendMode === 'test_codes';
      if (needsGiftSync && !busy && !selection.pending && !syncing && !error && reconcile) {
        syncing = true;
        void client.sync().then((current) => {
          notice = '';
          error = false;
          render(current.result);
          void refreshSections(current).catch(showError);
        }).catch(showError).finally(() => { syncing = false; });
      }
    }
    function showError() {
      error = true;
      notice = strings.error;
      if (lastResult) render(lastResult);
      document.querySelectorAll('[name="checkout"]').forEach((button) => {
        if (button.parentElement.querySelector('[data-bf-checkout-error]')) return;
        const message = document.createElement('p');
        message.dataset.bfCheckoutError = '';
        message.className = 'bf-gifts__checkout-error';
        message.setAttribute('role', 'alert');
        message.textContent = strings.error;
        button.insertAdjacentElement('beforebegin', message);
      });
    }
    function clearErrors() {
      error = false;
      notice = '';
      document.querySelectorAll('[data-bf-checkout-error]').forEach((node) => node.remove());
    }
    async function refreshSections(current) {
      const startedAt = Date.now();
      if (client.busy()) return;
      const version = mutationVersion;
      const sections = renderTargets();
      const rendered = { ...current?.cart.sections };
      const missing = [...new Set(sections.map((entry) => entry.section))].filter((id) => !rendered[id]);
      for (let offset = 0; offset < missing.length; offset += 5) {
        const ids = missing.slice(offset, offset + 5);
        const url = new URL(window.location.href);
        url.searchParams.set('sections', ids.join(','));
        const response = await nativeFetch(url, { cache: 'no-store' });
        if (!response.ok) throw new Error('Cart rendering failed / 购物车渲染失败');
        Object.assign(rendered, await response.json());
      }
      if (version !== mutationVersion || client.busy()) return;
      let recommendationsPreserved = 0;
      sections.forEach((entry) => {
          const node = document.getElementById(entry.id);
          const html = rendered[entry.section];
          if (!node || !html) return;
          const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(entry.selector);
          if (parsed && replaceCartSection(node.querySelector(entry.selector) || node, parsed)) recommendationsPreserved += 1;
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: current?.cart, giftResult: current?.result } }));
      reportTiming({ step: 'render-sections', elapsedMs: Date.now() - startedAt,
        fallbackSections: missing.length, recommendationsPreserved });
    }
    document.addEventListener('click', (event) => {
      const remove = event.target.closest('cart-remove-button');
      const row = remove?.closest('[data-bf-gift-variant]');
      if (!row || !lastResult?.data.dedicatedGiftVariantIds.some((id) => String(id) === row.dataset.bfGiftVariant)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (checkoutRunning || row.hasAttribute('data-bf-removing')) return;
      const quantity = Number(row.dataset.bfRequested ?? row.dataset.quantity);
      if (quantity > 0 && selections.enqueue(row.dataset.bfGiftVariant, -quantity)) {
        row.setAttribute('data-bf-removing', '');
        row.setAttribute('aria-busy', 'true');
        row.dataset.bfRequested = '0';
        const input = row.querySelector('[name="updates[]"]');
        if (input) input.value = '0';
      }
    }, true);
    document.addEventListener('change', async (event) => {
      const input = event.target;
      const row = input.closest('[data-bf-gift-variant]');
      if (input.name !== 'updates[]' || !row || !lastResult?.data.dedicatedGiftVariantIds
        .some((id) => String(id) === row.dataset.bfGiftVariant)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const previous = Number(row.dataset.bfRequested ?? row.dataset.quantity);
      const requested = Number(input.value);
      input.value = String(previous);
      if (checkoutRunning || !Number.isSafeInteger(requested) || requested < 0 || requested > rules.MAX_GIFTS) return;
      const startedAt = Date.now();
      if (selections.enqueue(row.dataset.bfGiftVariant, requested - previous)) {
        row.dataset.bfRequested = String(requested);
        input.value = String(requested);
        reportTiming({ step: 'selection-feedback', elapsedMs: Date.now() - startedAt });
      }
    }, true);
    document.addEventListener('click', async (event) => {
      const detail = event.target.closest('[data-bf-detail]');
      if (detail && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
        event.preventDefault();
        openDetails(detail.dataset.bfDetail, detail);
        return;
      }
      const edit = event.target.closest('[data-bf-edit]');
      if (edit) {
        event.preventDefault();
        editingGifts = true;
        if (lastResult) render(lastResult, { reconcile: false });
        return;
      }
      const retry = event.target.closest('[data-bf-retry]');
      if (retry) {
        event.preventDefault();
        if (busy || checkoutRunning) return;
        clearErrors();
        try { const current = await client.sync(); selections.recover(current.result); render(current.result); await refreshSections(current); } catch { showError(); }
        return;
      }
      const button = event.target.closest('[data-bf-delta]');
      if (!button) return;
      event.preventDefault();
      if (checkoutRunning) return;
      const startedAt = Date.now();
      if (selections.enqueue(button.dataset.bfVariant, Number(button.dataset.bfDelta))) {
        reportTiming({ step: 'selection-feedback', elapsedMs: Date.now() - startedAt });
      }
    });
    function returnFocus(origin) {
      const visible = (node) => node?.isConnected && node.getClientRects().length > 0 &&
        (!node.checkVisibility || node.checkVisibility());
      const opener = origin?.closest('cart-drawer')?.querySelector('summary') || document.querySelector('cart-drawer summary');
      const target = visible(origin) ? origin : visible(opener) ? opener
        : [...document.querySelectorAll('[name="checkout"]')].find(visible);
      const drawer = target?.closest('details[open]');
      if (drawer && typeof window.trapFocus === 'function') window.trapFocus(drawer, target);
      else target?.focus();
    }
    function openPicker(origin) {
      if (picker?.open) return;
      window.removeTrapFocus?.();
      picker = document.createElement('dialog');
      const panel = picker;
      panel.className = 'bf-gifts__picker';
      panel.setAttribute('aria-label', strings.title);
      panel.innerHTML = `<div class="bf-gifts__picker-header"><h2>${escape(strings.title)}</h2><button type="button" class="bf-gifts__picker-close" data-bf-picker-close aria-label="${escape(strings.close)}"><svg viewBox="0 0 16 16" width="20" height="20" aria-hidden="true"><path d="m3 3 10 10M13 3 3 13" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></button></div><div class="bf-gifts__picker-body"><div data-bf-gift-surface></div><section data-bf-detail-view hidden></section></div><div class="bf-gifts__picker-footer"><button type="button" class="button" data-bf-picker-done>${escape(strings.done)}</button></div>`;
      const close = () => { panel.close(); };
      panel.addEventListener('close', () => {
        panel.remove();
        if (picker === panel) picker = null;
        document.documentElement.classList.remove('bf-gifts-picker-open');
        returnFocus(origin);
      }, { once: true });
      panel.querySelector('[data-bf-picker-close]').onclick = close;
      panel.querySelector('[data-bf-picker-done]').onclick = close;
      document.body.append(panel);
      document.documentElement.classList.add('bf-gifts-picker-open');
      if (lastResult) render(lastResult, { reconcile: false });
      panel.showModal();
      (panel.querySelector('[data-bf-delta="1"]:not([disabled])') || panel.querySelector('[data-bf-picker-close]')).focus();
    }
    function openDetails(variantId, origin) {
      const gift = lastResult?.data.catalog.find((item) => String(item.variantId) === variantId);
      const url = gift && detailsUrl(gift);
      if (!url) return;
      openPicker(origin);
      const panel = picker;
      const body = panel.querySelector('.bf-gifts__picker-body');
      const list = panel.querySelector('[data-bf-gift-surface]');
      const detail = panel.querySelector('[data-bf-detail-view]');
      const scrollTop = body.scrollTop;
      const back = () => {
        list.hidden = false;
        detail.hidden = true;
        panel.querySelector('h2').textContent = strings.title;
        panel.setAttribute('aria-label', strings.title);
        body.scrollTop = scrollTop;
        panel.removeEventListener('cancel', cancel);
        const link = list.querySelector(`[data-bf-detail="${CSS.escape(variantId)}"]`);
        (link || panel.querySelector('[data-bf-picker-close]')).focus({ preventScroll: true });
      };
      const cancel = (event) => { event.preventDefault(); back(); };
      detail.innerHTML = `<button type="button" class="bf-gifts__back" data-bf-detail-back>${escape(strings.back)}</button>${gift.details.image ? `<img class="bf-gifts__detail-image" src="${escape(gift.details.image)}" alt="${escape(gift.title)}" width="720" height="720" decoding="async">` : ''}<p class="bf-gifts__description">${escape(decodeText(gift.details.description))}</p><a class="bf-gifts__full-details" href="${escape(url)}" target="_blank" rel="noopener">${escape(strings.full_details)}</a>`;
      detail.querySelector('[data-bf-detail-back]').onclick = back;
      list.hidden = true;
      detail.hidden = false;
      panel.querySelector('h2').textContent = gift.title;
      panel.setAttribute('aria-label', gift.title);
      body.scrollTop = 0;
      panel.addEventListener('cancel', cancel);
      detail.querySelector('[data-bf-detail-back]').focus({ preventScroll: true });
    }
    async function reminder(current, origin) {
      if (!current.result.enabled || !current.result.remainingSlots || !current.result.availableGifts.length) return true;
      const panel = document.createElement('dialog');
      panel.className = 'bf-gifts__checkout';
      panel.setAttribute('aria-label', strings.reminder);
      panel.innerHTML = `<p>${escape(strings.reminder)}</p><div class="bf-gifts__checkout-actions"><button type="button" class="button button--secondary" data-choose>${escape(strings.choose)}</button><button type="button" class="button" data-continue>${escape(strings.continue)}</button></div>`;
      document.body.append(panel);
      window.removeTrapFocus?.();
      panel.showModal();
      panel.querySelector('[data-choose]').focus();
      return new Promise((resolve) => {
        const finish = (proceed, choose = false) => {
          panel.close(); panel.remove();
          if (choose) {
            checkoutRunning = false;
            selections.pause(false);
            openPicker(origin);
          }
          else returnFocus(origin);
          resolve(proceed);
        };
        panel.addEventListener('cancel', (event) => { event.preventDefault(); finish(false); });
        panel.querySelector('[data-choose]').onclick = () => finish(false, true);
        panel.querySelector('[data-continue]').onclick = () => finish(true);
      });
    }
    document.addEventListener('submit', async (event) => {
      if (resumeCheckout || event.submitter?.name !== 'checkout') return;
      const form = event.target;
      if (!form.matches('form[action*="/cart"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (checkoutRunning) return;
      checkoutRunning = true;
      selections.pause(true);
      try {
        await selections.flush();
        const extra = codes(form.elements.namedItem('discount')?.value);
        let current = await client.checkout(extra);
        render(current.result);
        if (!(await reminder(current, event.submitter))) return;
        current = await client.checkout(extra);
        clearErrors();
        render(current.result);
        await refreshSections(current);
        const freshForm = event.submitter.closest('form')?.isConnected ? form
          : document.querySelector('details[open] form[action*="/cart"]') || document.querySelector('cart-items form[action*="/cart"]');
        const submitter = freshForm?.querySelector('[name="checkout"]') || document.querySelector('[name="checkout"][form="cart"]');
        if (!freshForm || !submitter) throw new Error('Checkout unavailable / 结账入口不可用');
        if (current.data.backendMode === 'test_codes' && current.result.selectedCount) {
          Array.from(freshForm.elements).filter((input) => input.name === 'discount')
            .forEach((input) => { input.value = mergedCodes(current.cart, current.data, extra); });
        }
        resumeCheckout = true;
        freshForm.requestSubmit(submitter);
        resumeCheckout = false;
      } catch { showError(); } finally {
        checkoutRunning = false;
        selections.pause(false);
        if (lastResult) render(lastResult, { reconcile: false });
      }
    }, true);
    window.LRBlackFridayGifts = { ...client, render, format, money,
      busy: () => client.busy() || selections.snapshot().pending,
      prepare: async (cart) => {
        const confirmedResult = client.displayFor(cart);
        if (confirmedResult && Date.now() - latestDataAt < 5000) return confirmedResult;
        if (Date.now() - latestDataAt < 5000 && fingerprint(cart) === fingerprint(latestData)) {
          return resultFor(cart, latestData);
        }
        return client.prepare(cart);
      },
      consumeMutationResponse: (response) => {
        const data = readResponseData(response);
        return data ? cartFromData(data) : null;
      },
      observeMutationResponse: (response) => client.observeResponse(response),
      bundleMutation: (args) => {
        const [input, options] = args;
        if (!options?.body || input instanceof Request) return args;
        const body = options.body;
        try {
          if (typeof body === 'string') {
            const value = JSON.parse(body);
            const sections = typeof value.sections === 'string' ? value.sections.split(',') : value.sections || [];
            if (sections.includes('black-friday-gifts-state') || sections.length >= 5) return args;
            return [input, { ...options, body: JSON.stringify({ ...value, sections: [...sections, 'black-friday-gifts-state'],
              sections_url: value.sections_url || window.location.pathname }) }];
          }
          if (body instanceof FormData) {
            const sections = String(body.get('sections') || '').split(',').filter(Boolean);
            if (sections.includes('black-friday-gifts-state') || sections.length >= 5) return args;
            const next = new FormData();
            body.forEach((value, key) => next.append(key, value));
            next.set('sections', [...sections, 'black-friday-gifts-state'].join(','));
            if (!next.has('sections_url')) next.set('sections_url', window.location.pathname);
            return [input, { ...options, body: next }];
          }
        } catch { /* Leave non-standard request bodies untouched. */ }
        return args;
      }
    };
  }
  return Object.freeze({ format, decodeText, decodeStrings, fingerprint, active, mergedCodes, freeGiftsConfirmed, resultFor, sectionTargets, replaceCartSection, createClient, createSelectionQueue, mount });
});
